import {HoistModel, managed, SelectOption, TaskObserver, XH} from '@xh/hoist/core';
import {action, observable, observableRef, runInAction} from '@xh/hoist/mobx';
import {RealEstateDeal, RealEstateStatusEnum, Security} from '../../../data/DataTypes';
import {orderBy} from 'lodash';
import {FormModel} from '@xh/hoist/cmp/form';
import {required} from '@xh/hoist/data';
import {getDefaultOptions} from '../../../../core/ServiceUtils';
import {dateOnlyToDateTime} from '../../../data/Utils';
import {GridModel} from '@xh/hoist/cmp/grid';
import React from 'react';

export class DealDialogModel extends HoistModel {
    private resolver: (value: RealEstateDeal) => void;
    apiTask = TaskObserver.trackLast();
    @observable accessor isOpen = false;
    @observableRef accessor securities: Security[] = [];
    @managed formModel = this.createFormModel();
    @managed docsModel = this.createGridModel();
    uploadRef = React.createRef<HTMLInputElement>();

    @action
    async openAsync(deal: RealEstateDeal): Promise<RealEstateDeal> {
        this.isOpen = true;
        this.resolver = () => {};
        this.initForm(deal);
        const securities = await XH.secMasterService.searchSecuritiesAsync('%');
        runInAction(() => (this.securities = securities));

        return new Promise(r => (this.resolver = r));
    }

    @action
    close() {
        this.isOpen = false;
        this.resolver(null);
    }

    get securitieOptions(): SelectOption[] {
        return orderBy(
            this.securities.map(s => ({value: s.id, label: s.name})),
            'label'
        );
    }

    get typeOptions(): string[] {
        return ['Multi-Asset', 'Office', 'Multifamily', 'Hospitality', 'Private Credit'];
    }

    get objectiveOptions(): string[] {
        return ['Income', 'Capital Appreciation', 'Value Add'];
    }

    get structureOptions(): string[] {
        return ['Equity', 'Senior Loan', '1st Lien', 'SPAC', 'BDC', 'Tender Fund'];
    }

    get taxOptions(): string[] {
        return ['1099-DIV', 'Opportunity Zone'];
    }

    get statusOptions(): string[] {
        return Object.values(RealEstateStatusEnum);
    }

    async save() {
        const deal = this.formModel.getData() as RealEstateDeal;
        deal.name = this.securitieOptions.find(s => s.value == deal.securityId).label;
        deal.inceptionDate = new Date(deal.inceptionDate).toISOString().substring(0, 10);
        if (deal.loanMaturityDate) deal.loanMaturityDate = new Date(deal.loanMaturityDate).toISOString().substring(0, 10);

        if (!deal.updatedBy) deal.updatedBy = XH.getUsername();
        if (!deal.updated) deal.updated = new Date().toISOString();

        try {
            await XH.postJson({
                url: `${XH.getConf('svcApiUrl')}/realestate/deals/save`,
                body: deal,
                ...getDefaultOptions()
            }).linkTo(this.apiTask);

            runInAction(() => (this.isOpen = false));
            this.resolver(deal);
        } catch (e) {
            XH.handleException(e);
        }
    }

    pickImage() {
        this.uploadRef.current.click();
    }

    uploadImage(file: File) {
        this.processFile(file);
        this.uploadRef.current.value = '';
    }

    get imageUrl(): string {
        return `${XH.getConf('svcApiUrl')}/media/images/${this.formModel.values.imageId}`;
    }

    private async processFile(file: File) {
        try {
            var reader = new FileReader();
            reader.readAsArrayBuffer(file);
            reader.onload = async e => {
                try {
                    const content = e.target.result as ArrayBuffer,
                        uintArray = new Uint8Array(content),
                        dataArray = [];
                    for (let i = 0; i < uintArray.length; i++) dataArray.push(uintArray[i]);

                    const imageId = await XH.fetchService.postJson({
                        url: `${XH.getConf('svcApiUrl')}/media/images`,
                        body: {fileName: file.name, contents: dataArray},
                        ...getDefaultOptions()
                    });

                    this.formModel.setValues({imageId: imageId});
                } catch (e) {
                    XH.handleException(e);
                }
            };
        } catch (e) {
            XH.handleException(e);
        }
        //});
        //return ret;
    }

    private initForm(deal: RealEstateDeal) {
        if (!deal) {
            this.formModel.init({id: 0, userId: 0});
        } else {
            this.formModel.init({
                ...deal,
                inceptionDate: dateOnlyToDateTime(deal.inceptionDate),
                loanMaturityDate: dateOnlyToDateTime(deal.loanMaturityDate)
            });
        }
    }

    private createFormModel(): FormModel {
        return new FormModel({
            fields: [
                {name: 'id'},
                {name: 'userId'},
                {name: 'securityId', rules: [required]},
                {name: 'status', rules: [required]},
                {name: 'sponsor', rules: [required]},
                {name: 'type', rules: [required]},
                {name: 'objective', rules: [required]},
                {name: 'structure', rules: [required]},
                {name: 'description', rules: [required]},
                {name: 'taxReporting', rules: [required]},
                {name: 'capital', rules: [required]},
                {name: 'inceptionDate', rules: [required]},
                {name: 'ltv'},
                {name: 'loanMaturityDate'},
                {name: 'imageId'},
                {name: 'updated'},
                {name: 'updatedBy'},
                {name: 'timestamp'}
            ]
        });
    }

    private createGridModel(): GridModel {
        return new GridModel({
            columns: [
                {field: 'id', hidden: true},
                {field: 'name', flex: 1},
                {field: 'type', width: 80},
                {field: 'date', width: 120}
            ]
        });
    }
}
